import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Zap, ChevronRight } from 'lucide-react';

export default function WelcomeScreen({ user, onContinue }) {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [isVisible, setIsVisible] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Trigger animation after mount
    setTimeout(() => setIsVisible(true), 100);

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleEnter();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleEnter = () => {
    // Clear welcome state before navigating
    if (onContinue) {
      onContinue();
    }
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#09090b] relative overflow-hidden flex items-center justify-center">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 grid-bg opacity-20"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-600/10 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>
        
        {/* Scanning Lines */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent animate-scan-line"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-4">
        {/* Logo */}
        <div 
          className={`transition-all duration-1000 transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
          }`}
        >
          <div className="inline-flex items-center justify-center w-24 h-24 bg-indigo-600/20 border-2 border-indigo-500/50 rounded-3xl mb-8 shadow-neon-indigo">
            <Shield className="w-14 h-14 text-indigo-400" style={{ filter: 'drop-shadow(0 0 10px rgba(99, 102, 241, 0.8))' }} />
          </div>
        </div>

        {/* Welcome Text */}
        <div 
          className={`transition-all duration-1000 delay-300 transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-indigo-400 font-mono text-sm mb-4 tracking-widest">
            WELCOME, {user?.full_name?.toUpperCase() || 'USER'}
          </p>
        </div>

        {/* Main Message - Animated */}
        <div 
          className={`transition-all duration-1000 delay-500 transform ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}
        >
          <button
            onClick={handleEnter}
            data-testid="enter-center-btn"
            className="group relative cursor-pointer focus:outline-none"
          >
            <div className="animate-pop-in-out">
              <h1 
                className="font-rajdhani font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-4 leading-tight"
                style={{ 
                  textShadow: '0 0 40px rgba(99, 102, 241, 0.6), 0 0 80px rgba(139, 92, 246, 0.4)'
                }}
              >
                Enter the Business
                <br />
                <span className="bg-gradient-to-r from-indigo-400 via-violet-400 to-cyan-400 bg-clip-text text-transparent">
                  Security Exposure Center
                </span>
              </h1>
            </div>
            
            {/* Click indicator */}
            <div className="flex items-center justify-center gap-2 text-indigo-400 group-hover:text-indigo-300 transition-colors mt-6">
              <Zap className="w-5 h-5 animate-pulse" />
              <span className="font-mono text-sm tracking-wide">CLICK TO ENTER</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>

        {/* Countdown */}
        <div 
          className={`mt-12 transition-all duration-1000 delay-700 transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-zinc-900/50 border border-white/10 rounded-full">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            <span className="text-zinc-400 font-mono text-sm">
              Auto-redirect in <span className="text-white font-bold">{countdown}</span> seconds
            </span>
          </div>
        </div>

        {/* Decorative Elements */}
        <div 
          className={`absolute -bottom-10 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-1000 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex items-center gap-8 text-zinc-600 text-xs font-mono">
            <span>CTEM</span>
            <span className="w-1 h-1 bg-zinc-600 rounded-full"></span>
            <span>THREAT INTELLIGENCE</span>
            <span className="w-1 h-1 bg-zinc-600 rounded-full"></span>
            <span>VULNERABILITY MANAGEMENT</span>
            <span className="w-1 h-1 bg-zinc-600 rounded-full"></span>
            <span>AI-POWERED</span>
          </div>
        </div>
      </div>

      {/* Corner Decorations */}
      <div className="absolute top-8 left-8 text-zinc-700 font-mono text-xs">
        <div>SYSTEM STATUS: <span className="text-emerald-500">ONLINE</span></div>
        <div className="mt-1">SECURITY LEVEL: <span className="text-indigo-400">MAXIMUM</span></div>
      </div>
      
      <div className="absolute top-8 right-8 text-zinc-700 font-mono text-xs text-right">
        <div>DEFENDLY AI v2.0</div>
        <div className="mt-1">{new Date().toLocaleDateString()}</div>
      </div>

      {/* CSS Animation Keyframes */}
      <style>{`
        @keyframes pop-in-out {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.03);
          }
        }
        
        @keyframes scan-line {
          0% {
            top: -2px;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            top: 100%;
            opacity: 0;
          }
        }
        
        .animate-pop-in-out {
          animation: pop-in-out 3s ease-in-out infinite;
        }
        
        .animate-scan-line {
          animation: scan-line 4s linear infinite;
        }
        
        .delay-500 {
          animation-delay: 0.5s;
        }
        
        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
}
